import * as Icons from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Canonical call action. Consumes backend-provided `options` (phone_options).
 *  - 0 options  -> disabled, "No phone number available"
 *  - 1 option   -> calls directly, accessible label "Call {Label} {value}"
 *  - 2 options  -> labeled chooser (never silently prefers one)
 * WhatsApp is intentionally NOT routed through here.
 */
export function PhoneCallMenu({ options = [], size = "sm", testid = "call-action" }) {
  const opts = (options || []).filter((o) => o && o.value);
  const dims = size === "sm" ? "h-8 w-8" : "h-9 w-9";

  if (opts.length === 0) {
    return (
      <button
        type="button" disabled data-testid={`${testid}-disabled`}
        title="No phone number available" aria-label="No phone number available"
        className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-slate-50 text-slate-300 cursor-not-allowed`}>
        <Icons.PhoneOff className="h-4 w-4" />
      </button>
    );
  }

  if (opts.length === 1) {
    const o = opts[0];
    return (
      <button
        type="button" data-testid={`${testid}-single`}
        onClick={() => { window.location.href = `tel:${o.value}`; }}
        title={`Call ${o.label} ${o.value}`} aria-label={`Call ${o.label} ${o.value}`}
        className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-brand-gold/10 text-brand-gold-dark hover:bg-brand-gold/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold`}>
        <Icons.PhoneCall className="h-4 w-4" />
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button" data-testid={`${testid}-menu`}
          title="Call" aria-label="Choose a number to call"
          className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-brand-gold/10 text-brand-gold-dark hover:bg-brand-gold/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold`}>
          <Icons.PhoneCall className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {opts.map((o) => (
          <DropdownMenuItem key={o.field || o.type}
            data-testid={`${testid}-opt-${o.type}`}
            aria-label={`Call ${o.label} ${o.value}`}
            onClick={() => { window.location.href = `tel:${o.value}`; }}>
            <Icons.Phone className="h-4 w-4 mr-2 text-slate-400" />
            <span className="flex flex-col">
              <span className="text-sm font-medium">Call {o.label}</span>
              <span className="text-xs text-slate-400">{o.value}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PhoneCallMenu;
